/**
 * SabbatsRing - Enhanced with CiH phase colors and improved rendering quality
 * 
 * Sabbats are mapped to seasons which map to CiH phases:
 * Winter sabbats (Yule, Imbolc) = NOURISH
 * Spring sabbats (Ostara, Beltane) = ACTIVATE  
 * Summer sabbats (Litha, Lammas) = FULFILL
 * Autumn sabbats (Mabon, Samhain) = INTEGRATE
 * 
 * SEGMENT BOUNDARY FIX (v1.93):
 * Segments are now calculated using midpoints between adjacent sabbats
 * to eliminate gaps and overlaps caused by uneven sabbat spacing.
 */
import React from 'react';
import { motion } from 'framer-motion';
import { SABBATS_NORTHERN, SABBATS, getSabbatsForHemisphere, createArcPath, createSegmentPath, dayOfYearToDialAngle } from './clockUtils';
import { getCihPhaseColor } from '../utils/styleCard';

/**
 * CiH Phase Mapping for Sabbats
 * 
 * The Wheel of the Year represents the eternal cycle of seasons.
 * Each sabbat marks a transition point between phases:
 * 
 * NOURISH (Winter → Early Spring): Rest, reflection, dormancy
 *   - Yule (Winter Solstice) - Deepest NOURISH
 *   - Imbolc (Feb 1) - NOURISH transitioning to ACTIVATE
 * 
 * ACTIVATE (Spring → Early Summer): Growth, emergence, action
 *   - Ostara (Spring Equinox) - Pure ACTIVATE
 *   - Beltane (May 1) - Peak ACTIVATE/fertility
 * 
 * FULFILL (Summer → Early Autumn): Abundance, harvest beginning
 *   - Litha (Summer Solstice) - Peak FULFILL/light
 *   - Lammas/Lughnasadh (Aug 1) - First harvest, FULFILL
 * 
 * INTEGRATE (Autumn → Early Winter): Harvest completion, letting go
 *   - Mabon (Autumn Equinox) - Balance, INTEGRATE
 *   - Samhain (Nov 1) - Death/rebirth, deepest INTEGRATE
 */
const SABBAT_CIH_MAPPING = {
  'Yule': 'NOURISH',        // Winter Solstice - deepest rest
  'Imbolc': 'NOURISH',      // Late Winter - stirring beneath
  'Ostara': 'ACTIVATE',     // Spring Equinox - emergence
  'Beltane': 'ACTIVATE',    // Peak Spring - growth/fertility
  'Litha': 'FULFILL',       // Summer Solstice - peak light
  'Lammas': 'FULFILL',      // First Harvest
  'Lughnasadh': 'FULFILL',  // (alternate name)
  'Mabon': 'INTEGRATE',     // Autumn Equinox - balance
  'Samhain': 'INTEGRATE'    // Late Autumn - release
};

const getSabbatCihPhase = (sabbatName) => {
  return SABBAT_CIH_MAPPING[sabbatName] || 'ACTIVATE';
};

/**
 * Calculate segment boundaries as midpoints between adjacent sabbats
 * This eliminates gaps and overlaps from uneven spacing
 */
const calculateSegmentBoundaries = (sabbats) => {
  const n = sabbats.length;
  return sabbats.map((sabbat, i) => {
    const prevSabbat = sabbats[(i - 1 + n) % n];
    const nextSabbat = sabbats[(i + 1) % n];
    
    const thisAngle = dayOfYearToDialAngle(sabbat.day);
    const prevAngle = dayOfYearToDialAngle(prevSabbat.day);
    const nextAngle = dayOfYearToDialAngle(nextSabbat.day);
    
    // Calculate midpoint to previous (handling wrap-around at 0°/360°)
    let startAngle;
    let diffToPrev = thisAngle - prevAngle;
    if (diffToPrev < -180) diffToPrev += 360;
    if (diffToPrev > 180) diffToPrev -= 360;
    startAngle = thisAngle - diffToPrev / 2;
    
    // Calculate midpoint to next (handling wrap-around)
    let endAngle;
    let diffToNext = nextAngle - thisAngle;
    if (diffToNext < -180) diffToNext += 360;
    if (diffToNext > 180) diffToNext -= 360;
    endAngle = thisAngle + diffToNext / 2;
    
    return {
      ...sabbat,
      centerAngle: thisAngle,
      startAngle,
      endAngle
    };
  });
};

const SabbatsRing = ({ center, radius, currentDate, onSabbatClick, locationIntelligence }) => {
  // Scaled ring dimensions for better quality at zoom
  const ringWidth = 29;
  const innerRadius = radius - ringWidth;
  const textRadius = radius - ringWidth / 2;
  const currentDay = Math.floor((currentDate - new Date(currentDate.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
  const pathIdPrefix = `sabbat-path-${center}`;
  
  // Get hemisphere-appropriate sabbats
  const isSouthern = locationIntelligence?.is_southern || locationIntelligence?.hemisphere === 'south';
  const SABBATS = getSabbatsForHemisphere(isSouthern);
  
  // Calculate proper segment boundaries (midpoints between sabbats)
  const sabbatsWithBoundaries = calculateSegmentBoundaries(SABBATS);

  // Calculate which sabbat is next and previous for dual countdown context
  // For southern hemisphere, shift by ~182 days (half year) so the HIGHLIGHT
  // appears on the opposite sabbat (e.g., Mabon instead of Ostara in March)
  // This matches the inverted zodiac/planetary positions for southern regions
  const adjustedDay = isSouthern ? (currentDay + 182) % 365 : currentDay;
  
  const sabbatsWithDays = sabbatsWithBoundaries.map(s => ({ 
    ...s, 
    daysUntil: (s.day - adjustedDay + 365) % 365,
    daysSince: (adjustedDay - s.day + 365) % 365
  })).sort((a, b) => a.daysUntil - b.daysUntil);
  
  const nextSabbat = sabbatsWithDays[0];
  const previousSabbat = sabbatsWithDays[sabbatsWithDays.length - 1];

  return (
    <motion.g 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      transition={{ delay: 0.15, duration: 0.5 }} 
      data-testid="sabbats-ring"
      style={{ 
        shapeRendering: 'geometricPrecision'
      }}
    >
      <defs>
        {/* Enhanced gradient definitions using CiH phase colors */}
        {sabbatsWithBoundaries.map((sabbat, index) => {
          const cihPhase = getSabbatCihPhase(sabbat.name);
          const cihColor = getCihPhaseColor(cihPhase);
          
          return (
            <React.Fragment key={`sabbat-defs-${sabbat.name}`}>
              {/* Radial gradient for segment fill - CiH colors */}
              <radialGradient 
                id={`sabbat-gradient-${sabbat.name}`}
                cx="50%" cy="50%" r="100%"
              >
                <stop offset="0%" stopColor={cihColor} stopOpacity="0.7" />
                <stop offset="100%" stopColor={cihColor} stopOpacity="0.4" />
              </radialGradient>
              
              {/* Text path - using calculated boundaries */}
              <path 
                id={`${pathIdPrefix}-${index}`}
                d={createArcPath(center, textRadius, sabbat.startAngle, sabbat.endAngle)} 
                fill="none" 
              />
            </React.Fragment>
          );
        })}
      </defs>

      {/* Ring borders with enhanced stroke */}
      <circle 
        cx={center} 
        cy={center} 
        r={radius} 
        fill="none" 
        stroke="#C5A059" 
        strokeWidth="1.5" 
        opacity="0.5"
        style={{ shapeRendering: 'geometricPrecision' }}
      />
      <circle 
        cx={center} 
        cy={center} 
        r={innerRadius} 
        fill="none" 
        stroke="#C5A059" 
        strokeWidth="1.5" 
        opacity="0.4"
        style={{ shapeRendering: 'geometricPrecision' }}
      />

      {/* Render ALL segment fills using calculated midpoint boundaries */}
      {sabbatsWithBoundaries.map((sabbat, index) => {
        // Use pre-calculated boundaries (midpoints between sabbats)
        const { startAngle, endAngle, centerAngle } = sabbat;
        const { path } = createSegmentPath(center, innerRadius, radius, startAngle, endAngle);
        const isNext = sabbat.name === nextSabbat?.name;
        const isPrevious = sabbat.name === previousSabbat?.name;
        
        // Get CiH phase color - THIS IS THE PRIMARY COLOR for the sabbat
        const cihPhase = getSabbatCihPhase(sabbat.name);
        const cihColor = getCihPhaseColor(cihPhase);

        return (
          <motion.g 
            key={sabbat.name} 
            style={{ cursor: 'pointer' }} 
            whileHover={{ scale: 1.02 }}
            onClick={() => onSabbatClick?.('sabbat', sabbat.name)}
            data-testid={`sabbat-marker-${sabbat.name.toLowerCase()}`}
          >
            {/* Segment fill - USE CiH PHASE COLOR */}
            <motion.path 
              d={path} 
              fill={isNext ? `url(#sabbat-gradient-${sabbat.name})` : cihColor}
              opacity={isNext ? 0.55 : isPrevious ? 0.35 : 0.25}
              stroke="none"
              style={{ shapeRendering: 'geometricPrecision' }}
              whileHover={{ opacity: 0.6 }} 
            />
            
            {/* Sabbat name text with CiH phase color */}
            <text 
              fill={isNext ? '#FFFFFF' : cihColor} 
              fontSize={isNext ? "24" : "21"} 
              fontWeight={isNext ? 'bold' : '500'}
              fontFamily="'Rajdhani', serif"
              style={{ 
                pointerEvents: 'none', 
                textShadow: isNext ? `0 0 8px ${cihColor}` : 'none',
                textRendering: 'optimizeLegibility'
              }}
            >
              <textPath 
                href={`#${pathIdPrefix}-${index}`} 
                startOffset="50%" 
                textAnchor="middle"
              >
                {sabbat.name}
              </textPath>
            </text>
            
            {/* Highlight outline for next sabbat segment */}
            {isNext && (
              <motion.path
                d={path}
                fill="none"
                stroke={cihColor}
                strokeWidth={2}
                opacity={0.7}
                style={{ shapeRendering: 'geometricPrecision' }}
                animate={{ 
                  opacity: [0.5, 0.8, 0.5]
                }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
            )}
            
            {/* Highlight marker for next sabbat */}
            {isNext && (
              <motion.circle
                cx={center + (radius - ringWidth/2) * Math.cos((centerAngle - 90) * Math.PI / 180)}
                cy={center + (radius - ringWidth/2) * Math.sin((centerAngle - 90) * Math.PI / 180)}
                r={4}
                fill={cihColor}
                opacity={0.8}
                animate={{ 
                  scale: [1, 1.3, 1],
                  opacity: [0.8, 1, 0.8]
                }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
            )}
          </motion.g>
        );
      })}
      
      {/* Render all division lines AFTER segments at exact boundaries */}
      {sabbatsWithBoundaries.map((sabbat, index) => {
        // Use exact calculated boundary (startAngle is midpoint to previous)
        const angleRad = (sabbat.startAngle - 90) * Math.PI / 180;
        const x1 = center + innerRadius * Math.cos(angleRad);
        const y1 = center + innerRadius * Math.sin(angleRad);
        const x2 = center + radius * Math.cos(angleRad);
        const y2 = center + radius * Math.sin(angleRad);
        
        return (
          <line
            key={`div-${sabbat.name}`}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke="#4a3d20"
            strokeWidth={1}
            opacity={0.8}
            style={{ shapeRendering: 'crispEdges' }}
          />
        );
      })}
    </motion.g>
  );
};

export default SabbatsRing;
